import { ref, get, set, push, serverTimestamp, update, onValue, query, orderByChild } from 'firebase/database';
import { database } from '../firebase';

/**
 * Lista todas as conversas com nutricionistas
 */
export async function getAllAdminChats() {
  try {
    const adminChatRef = ref(database, 'adminChat');
    const snapshot = await get(adminChatRef);

    if (!snapshot.exists()) {
      return [];
    }

    const chatsData = snapshot.val();
    const chats = [];

    for (const [nutritionistId, chatData] of Object.entries(chatsData)) {
      if (chatData.metadata) {
        chats.push({
          nutritionistId,
          ...chatData.metadata,
        });
      }
    }

    // Ordenar por última mensagem (mais recente primeiro)
    chats.sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));

    return chats;
  } catch (error) {
    console.error('Erro ao buscar conversas:', error);
    return [];
  }
}

/**
 * Busca mensagens de uma conversa específica
 */
export async function getAdminChatMessages(nutritionistId) {
  if (!nutritionistId) return [];

  try {
    const messagesRef = ref(database, `adminChat/${nutritionistId}/messages`);
    const messagesSnap = await get(messagesRef);

    if (!messagesSnap.exists()) {
      return [];
    }

    const messagesData = messagesSnap.val();
    const messages = [];

    Object.entries(messagesData).forEach(([id, data]) => {
      const timestamp = data.createdAt || data.timestamp || '';
      let rawTimestamp = 0;

      if (typeof timestamp === 'object' && timestamp.seconds) {
        rawTimestamp = timestamp.seconds * 1000;
      } else if (typeof timestamp === 'string') {
        rawTimestamp = new Date(timestamp).getTime();
      } else if (typeof timestamp === 'number') {
        rawTimestamp = timestamp;
      }

      messages.push({
        id,
        author: data.author || 'nutritionist',
        text: data.text || '',
        createdAt: formatChatTime(timestamp),
        image: data.image,
        audio: data.audio,
        rawTimestamp,
      });
    });

    // Ordenar por timestamp (mais antigas primeiro)
    messages.sort((a, b) => a.rawTimestamp - b.rawTimestamp);

    return messages.map(({ rawTimestamp, ...msg }) => msg);
  } catch (error) {
    console.error('Erro ao buscar mensagens:', error);
    return [];
  }
}

/**
 * Formata timestamp para exibição
 */
function formatChatTime(value) {
  if (!value) return '';

  try {
    let date;

    if (typeof value === 'object' && value.seconds) {
      date = new Date(value.seconds * 1000);
    } else if (typeof value === 'string') {
      date = new Date(value);
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else {
      return '';
    }

    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return `Ontem ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      return `${days[date.getDay()]} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  } catch {
    return '';
  }
}

/**
 * Envia mensagem para nutricionista
 */
export async function sendMessageToNutritionist(nutritionistId, text, imageUrl, audioUrl) {
  if (!nutritionistId || (!text?.trim() && !imageUrl && !audioUrl)) return;

  try {
    // Buscar metadados
    const metadataRef = ref(database, `adminChat/${nutritionistId}/metadata`);
    const metadataSnap = await get(metadataRef);

    let metadata = null;
    if (metadataSnap.exists()) {
      metadata = metadataSnap.val();
    } else {
      // Se não existe, buscar dados do nutricionista
      const userRef = ref(database, `users/${nutritionistId}`);
      const userSnap = await get(userRef);

      if (!userSnap.exists()) {
        throw new Error('Nutricionista não encontrado');
      }

      const userData = userSnap.val();
      metadata = {
        nutritionistId,
        nutritionistName: userData.name || 'Nutricionista',
        nutritionistEmail: userData.email || '',
        lastMessage: '',
        lastMessageTime: Date.now(),
        unreadCount: 0,
        unreadCountNutritionist: 0,
      };

      await set(metadataRef, metadata);
    }

    // Enviar mensagem
    const messagesRef = ref(database, `adminChat/${nutritionistId}/messages`);
    const newMsgRef = push(messagesRef);
    const messageData = {
      author: 'admin',
      text: text || '',
      createdAt: serverTimestamp(),
    };

    if (imageUrl) {
      messageData.image = imageUrl;
    }

    if (audioUrl) {
      messageData.audio = audioUrl;
    }

    await set(newMsgRef, messageData);

    // Atualizar metadados
    const lastMessage = text || (imageUrl ? '📷 Imagem' : '🎤 Áudio');
    await update(metadataRef, {
      lastMessage,
      lastMessageTime: Date.now(),
      unreadCountNutritionist: (metadata.unreadCountNutritionist || 0) + 1,
    });
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    throw error;
  }
}

/**
 * Marca mensagens como lidas pelo admin
 */
export async function markAsRead(nutritionistId) {
  if (!nutritionistId) return;

  try {
    const metadataRef = ref(database, `adminChat/${nutritionistId}/metadata`);
    const metadataSnap = await get(metadataRef);

    if (!metadataSnap.exists()) return;

    await update(metadataRef, {
      unreadCount: 0,
    });
  } catch (error) {
    console.error('Erro ao marcar como lida:', error);
  }
}

/**
 * Listener em tempo real para lista de conversas
 */
export function subscribeToAllAdminChats(callback) {
  const adminChatRef = ref(database, 'adminChat');

  const unsubscribe = onValue(adminChatRef, async () => {
    const chats = await getAllAdminChats();
    callback(chats);
  }, (error) => {
    console.error('Erro no listener de conversas:', error);
    callback([]);
  });

  return unsubscribe;
}

/**
 * Listener em tempo real para mensagens de uma conversa
 */
export function subscribeToAdminChatMessages(nutritionistId, callback) {
  if (!nutritionistId) {
    return () => {};
  }

  const messagesRef = ref(database, `adminChat/${nutritionistId}/messages`);
  const messagesQuery = query(messagesRef, orderByChild('createdAt'));

  const unsubscribe = onValue(messagesQuery, async () => {
    const messages = await getAdminChatMessages(nutritionistId);
    callback(messages);
  }, (error) => {
    console.error('Erro no listener de mensagens:', error);
    callback([]);
  });

  return unsubscribe;
}

/**
 * Listener em tempo real para metadados de uma conversa
 */
export function subscribeToAdminChatMetadata(nutritionistId, callback) {
  if (!nutritionistId) {
    return () => {};
  }

  const metadataRef = ref(database, `adminChat/${nutritionistId}/metadata`);

  const unsubscribe = onValue(metadataRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Erro no listener de metadados:', error);
    callback(null);
  });

  return unsubscribe;
}








